import React from "react";
import { Box, Link, Typography, makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  getTicketEventsNavPaths,
  isProofOfTicketRoute,
  isTicketHolderRoute,
  PREVIEW_PROOF,
  PREVIEW_TICKET,
} from "./ticketEventsNavPaths";

export const TICKET_EVENTS_LOGO = "/Startfish_Black_192.png";

const PROOF_BAR_BG = "#2ecc71";

const useStyles = makeStyles((theme) => ({
  header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minHeight: 56,
    padding: theme.spacing(1.5, 2),
    boxSizing: "border-box",
    backgroundColor: "#000",
    background: "#000",
    isolation: "isolate",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
    transition: "box-shadow 0.25s ease",
  },
  headerProof: {
    backgroundColor: PROOF_BAR_BG,
    background: PROOF_BAR_BG,
    borderBottom: "1px solid rgba(0,0,0,0.12)",
  },
  headerScrolled: {
    boxShadow: "0 2px 12px rgba(0,0,0,0.45)",
  },
  headerScrolledProof: {
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerLogo: {
    height: 48,
    width: "auto",
    display: "block",
    objectFit: "contain",
  },
  headerTitle: {
    color: "#fff",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 600,
    fontSize: "1.35rem",
    letterSpacing: "0.02em",
  },
  headerTitleProof: {
    color: "#000",
  },
  headerNav: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      justifyContent: "flex-end",
    },
  },
  headerNavLink: {
    color: "rgba(255,255,255,0.85)",
    fontSize: "1rem",
    cursor: "pointer",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    "&:hover": {
      color: "#fff",
    },
  },
  headerNavLinkActive: {
    color: "#fff",
    fontWeight: 600,
    borderBottom: "2px solid #fff",
    paddingBottom: 2,
  },
  headerNavLinkProof: {
    color: "rgba(0,0,0,0.75)",
    fontSize: "1rem",
    cursor: "pointer",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    "&:hover": {
      color: "#000",
    },
  },
  headerNavLinkActiveProof: {
    color: "#000",
    fontWeight: 600,
    borderBottom: "2px solid #000",
    paddingBottom: 2,
  },
}));

export default function TicketEventsHeader({
  scrolled,
  variant = "ticketHolder",
  proofSurfaceColor,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const paths =
    getTicketEventsNavPaths(pathname) ?? {
      ticketHolder: PREVIEW_TICKET,
      proof: PREVIEW_PROOF,
    };

  const lang =
    i18n.language && i18n.language.startsWith("fr") ? "fr" : "en";

  const onProof = isProofOfTicketRoute(pathname);
  const onTicketHolder = isTicketHolderRoute(pathname);

  // One active underline: proof page → only Proof; ticket holder → only EN or FR.
  const enActive = onTicketHolder && lang === "en";
  const frActive = onTicketHolder && lang === "fr";
  const proofActive = onProof;

  const isProofTheme = variant === "proof";
  const proofBg = proofSurfaceColor ?? PROOF_BAR_BG;
  const linkBase = isProofTheme ? classes.headerNavLinkProof : classes.headerNavLink;
  const linkActive = (active) =>
    clsx(
      linkBase,
      active &&
        (isProofTheme ? classes.headerNavLinkActiveProof : classes.headerNavLinkActive),
    );

  return (
    <header
      className={clsx(
        classes.header,
        isProofTheme && classes.headerProof,
        scrolled &&
          (isProofTheme ? classes.headerScrolledProof : classes.headerScrolled),
      )}
      style={
        isProofTheme ? { backgroundColor: proofBg } : { backgroundColor: "#000" }
      }
    >
      <Box className={classes.headerLeft}>
        <img src={TICKET_EVENTS_LOGO} alt="" className={classes.headerLogo} />
        <Typography
          component="span"
          className={clsx(classes.headerTitle, isProofTheme && classes.headerTitleProof)}
        >
          {t("ticketHolderNav")}
        </Typography>
      </Box>
      <Box className={classes.headerNav}>
        <Link
          component={RouterLink}
          to={paths.ticketHolder}
          color="inherit"
          underline="none"
          className={linkActive(enActive)}
          onClick={() => i18n.changeLanguage("en")}
        >
          {t("ticketHolderNavEnglish")}
        </Link>
        <Link
          component={RouterLink}
          to={paths.ticketHolder}
          color="inherit"
          underline="none"
          className={linkActive(frActive)}
          onClick={() => i18n.changeLanguage("fr")}
        >
          {t("ticketHolderNavFrench")}
        </Link>
        <Link
          component={RouterLink}
          to={paths.proof}
          color="inherit"
          underline="none"
          className={linkActive(proofActive)}
        >
          {t("ticketHolderNavProof")}
        </Link>
      </Box>
    </header>
  );
}
